import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router';
import { analyticsAllowed } from '@il-colpo/shared/account';
import { useAccount } from '../account/accountStore.ts';
import { setAnalyticsConsent, track } from './analytics.ts';

/** The first part of an address, which names the part of the app (and never carries a code or an id). */
export function screenOf(pathname: string): string {
  return pathname.split('/')[1] || 'home';
}

/**
 * Follows the player's analytics consent (the privacy screen's switch, kept with the account) and, with a
 * yes, tells the app was opened and where the player goes. Mounted once, in the layout.
 */
export function useAnalytics(): void {
  const consents = useAccount((s) => s.consents);
  const granted = consents !== null && analyticsAllowed(consents);
  const { pathname } = useLocation();
  const screen = screenOf(pathname);
  const opened = useRef(false);
  const lastScreen = useRef<string | null>(null);

  useEffect(() => {
    setAnalyticsConsent(granted);
    if (!granted) {
      opened.current = false;
      lastScreen.current = null;
    }
  }, [granted]);

  useEffect(() => {
    if (!granted) return;
    if (!opened.current) {
      opened.current = true;
      lastScreen.current = screen;
      track('app_opened', { screen });
    } else if (lastScreen.current !== screen) {
      lastScreen.current = screen;
      track('screen_view', { screen });
    }
  }, [granted, screen]);
}
